import { useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useCachedFetch } from './useCachedFetch';

/** Listado de expedientes para agrupar por `parcela_id` en ExpedientesPage. */
export interface ExpedienteListRow {
  /** UUID PK del expediente — usado por DELETE /v1/upload/expediente/:id. */
  id: string;
  /**
   * Código público de la parcela (HUM-P0042 / COR-P0099). NO el UUID FK.
   * `expedientes.parcela_id` en DB es UUID → joineamos con `parcelas` para
   * exponer aquí el código público que la UI usa para filtrar/agrupar.
   */
  parcela_id: string;
  tipo_doc: string;
  r2_key: string;
  size_bytes: number;
  /** Mantenemos el nombre `subido_at` en el tipo (UI lo usa así) pero
   *  mapeamos desde `created_at` que es la columna real en DB. */
  subido_at: string;
}

/** Shape crudo del SELECT con embed PostgREST: `parcela:parcelas(parcela_id)`. */
type ExpedienteRowRaw = {
  id: string;
  tipo_doc: string;
  r2_key: string;
  size_bytes: number;
  subido_at: string;
  // PostgREST puede serializar el embed como objeto único o array según la
  // cardinalidad detectada. Normalizamos abajo.
  parcela: { parcela_id: string | null } | { parcela_id: string | null }[] | null;
};

export function useExpedientesList(): {
  data: ExpedienteListRow[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<ExpedienteListRow[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    // Embed `parcelas` via FK `expedientes.parcela_id → parcelas.id`. El
    // alias `parcela:parcelas(parcela_id)` saca el código público
    // (parcelas.parcela_id, tipo text "HUM-P0042"), no el UUID.
    // Sin esto, la UI filtraba por código público contra UUIDs y siempre
    // devolvía vacío tras subir.
    const { data: rows, error: err } = await sb
      .from('expedientes')
      .select('id, tipo_doc, r2_key, size_bytes, subido_at:created_at, parcela:parcelas(parcela_id)')
      .order('created_at', { ascending: false });
    if (err) throw err;
    const raw = (rows as unknown as ExpedienteRowRaw[]) ?? [];
    return raw.flatMap<ExpedienteListRow>((r) => {
      const par = Array.isArray(r.parcela) ? r.parcela[0] : r.parcela;
      const code = par?.parcela_id;
      // Sin código público no hay forma de joinear con la UI; descartamos
      // (caso teórico: parcela huérfana, FK rota — no debería pasar con
      // ON DELETE RESTRICT).
      if (!code) return [];
      return [{
        id: r.id,
        parcela_id: code,
        tipo_doc: r.tipo_doc,
        r2_key: r.r2_key,
        size_bytes: r.size_bytes,
        subido_at: r.subido_at,
      }];
    });
  }, []);

  // TTL 30s — los expedientes cambian al hacer upload, conviene revalidar
  // más rápido que parcelas/inventario.
  const { data, loading, error, refetch } = useCachedFetch<ExpedienteListRow[]>(
    'expedientes_list',
    fetcher,
    { ttlMs: 30_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
